/**
 * Data Assets Intelligence API client.
 *
 * All calls go to the PepWise Node backend, which authenticates the user and
 * forwards the request to the Python service that owns the data. Responses are
 * unwrapped from PepWise's `{ success, statusCode, data }` envelope so the
 * pages see exactly the payload the Python API returns.
 */
import axios from 'axios';
import { url } from 'service/config';
import { getAuthorazieHeader } from 'service/service';

const BASE = `${url}/dataAssetIntelligence`;

export interface ApiResult<T = any> {
	ok: boolean;
	status: number;
	data: T;
	error?: string;
}

type Params = Record<string, any> | undefined;

async function request(
	method: 'get' | 'post',
	path: string,
	{ params, body }: { params?: Params; body?: any } = {}
): Promise<ApiResult> {
	try {
		const response = await axios({
			method,
			url: `${BASE}${path}`,
			params,
			data: body,
			headers: getAuthorazieHeader()
		});
		return { ok: true, status: response.status, data: response.data?.data ?? response.data };
	} catch (err: any) {
		return {
			ok: false,
			status: err?.response?.status ?? 0,
			data: err?.response?.data?.data ?? null,
			error: err?.response?.data?.data?.message || err?.message || 'Request failed'
		};
	}
}

const get = (path: string, params?: Params) => request('get', path, { params });
const post = (path: string, body?: any) => request('post', path, { body });

export const api = {
	authCheck: () => get('/auth-check'),

	// ---- Silver -------------------------------------------------------------
	status: (date?: string) => get('/silver', date ? { date } : undefined),
	refresh: () => get('/silver/refresh'),
	detail: (asset: string) => get(`/silver/asset/${encodeURIComponent(asset)}`),
	silverReport: (date?: string) => get('/silver/report', date ? { date } : undefined),
	silverHourly: (params?: Params) => get('/silver/hourly', params),

	histStats: (params?: Params) => get('/silver/history', params),
	histStatsRefresh: () => post('/silver/history/refresh'),
	histStatsStatus: () => get('/silver/history/status'),

	scheduleRules: (asset: string) => get('/silver/schedule', { asset }),
	saveScheduleRules: (payload: any) => post('/silver/schedule', payload),
	resetScheduleRules: (asset: string) => post('/silver/schedule/reset', { asset }),

	volumeStats: (params?: Params) => get('/silver/volume-stats', params),
	volumeStatsStatus: () => get('/silver/volume-stats/status'),
	volumeStatsRefresh: () => post('/silver/volume-stats/refresh'),
	volumePrediction: (params?: Params) => get('/silver/volume-prediction', params),

	// ---- Gold ---------------------------------------------------------------
	goldStatus: (params?: Params) => get('/gold', params),
	goldHourly: (params?: Params) => get('/gold/hourly', params),
	goldRefreshDaily: (date?: string) => post('/gold/refresh', { date }),
	goldRefreshHistory: () => post('/gold/refresh/history'),
	goldRefreshStatus: () => get('/gold/refresh/status'),
	goldReference: (params?: Params) => get('/gold/reference', params),
	goldReferenceUpdate: (payload: any) => post('/gold/reference', payload),
	goldReferenceClearCache: () => post('/gold/reference/clear-cache'),
	goldReferenceResetSchedule: (payload: any) => post('/gold/reference/reset-schedule', payload),
	goldMarkets: () => get('/gold/markets'),
	goldJobDetail: (params?: Params) => get('/gold/job-detail', params),
	goldJobDependencies: (jobName: string) => get('/gold/job-dependencies', { job_name: jobName }),
	goldReport: (params?: Params) => get('/gold/report', params),
	goldReportExport: (params?: Params) => get('/gold/report/export', params),

	// ---- Asset tagging ------------------------------------------------------
	assetTaggingList: (source: string, q?: string) => get(`/asset-tagging/${source}`, q ? { q } : undefined),
	assetTaggingSync: (source: string) => post('/asset-tagging/sync', { source }),
	assetTaggingToggle: (source: string, payload: any) => post(`/asset-tagging/${source}/toggle`, payload),
	assetTaggingUpsert: (source: string, payload: any) => post(`/asset-tagging/${source}/upsert`, payload),
	assetTaggingApplication: (payload: any) => post('/asset-tagging/silver/application', payload),

	// ---- Iberia -------------------------------------------------------------
	iberiaStatus: (date?: string) => get('/iberia', date ? { date } : undefined),
	iberiaSchedule: (params?: Params) => get('/iberia/schedule', params),
	iberiaDatasetDetail: (dataset: string, date?: string) => get(`/iberia/asset/${encodeURIComponent(dataset)}`, date ? { date } : undefined),
	iberiaJobDetail: (jobId: string) => get('/iberia/job-detail', { job_id: jobId }),
	iberiaJobRuns: (jobId: string, date?: string) => get('/iberia/job-runs', { job_id: jobId, ...(date ? { date } : {}) }),
	iberiaScheduleRule: (payload: any) => post('/iberia/schedule-rule', payload),
	iberiaScheduleDay: (payload: any) => post('/iberia/schedule-day', payload),
	iberiaScheduleDate: (payload: any) => post('/iberia/schedule-date', payload),
	iberiaExpectedDays: (payload: any) => post('/iberia/expected-days', payload),
	iberiaExpectedDates: (payload: any) => post('/iberia/expected-dates', payload)
};

export default api;
